import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Box, IconButton, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import LoadingOverlay from "../../../../components/common/loader/OverlayLoader";
import ButtonLoader from "../../../../components/common/loader/ButtonLoader";
import { getRepairOrderRAGDetail } from "../../../../redux/features/inventories/inventoriesAction";
import InRepairPriorityBadge from "../components/InRepairPriorityBadge";
import InRepairWorkstreamBadge from "../components/InRepairWorkstreamBadge";
import InRepairScGroupBadge from "../components/InRepairScGroupBadge";
import InRepairStatusBadge from "../components/InRepairStatusBadge";
import RoDetailCard from "../components/ro-detail/RoDetailCard";
import RoDetailField from "../components/ro-detail/RoDetailField";
import RoDetailSectionLabel from "../components/ro-detail/RoDetailSectionLabel";
import RoDetailPhaseHistoryIcon from "../components/ro-detail/RoDetailPhaseHistoryIcon";
import RoDetailPartsTable from "../components/ro-detail/RoDetailPartsTable";
import {
  RO_DETAIL_COLORS,
  RO_DETAIL_FONT,
  RO_DETAIL_LAYOUT,
  RO_DETAIL_DEFAULT_WORKFLOW_STEPPER,
  RO_DETAIL_EMPTY_MESSAGES,
  RO_DETAIL_QUERY_ID,
  RO_DETAIL_QUERY_RO,
  RO_DETAIL_PART_NUMBERS_KEY,
} from "../data/inRepairRoDetailConstants";
import { IN_REPAIR_RAG_COLORS } from "../data/inRepairListingConstants";
import {
  getRoDetailSubtitleParts,
  getRoDetailPhaseHistoryLines,
  fmtRoDetailCurrency,
  fmtRoDetailDate,
  fmtRoDetailDays,
  fmtRoDetailDaysToEdd,
  fmtRoDetailNumber,
  fmtRoDetailPercent,
  fmtRoDetailTatDays,
  getRoDetailBackLabel,
  getRoDetailEconFlagColor,
  getRoDetailSlaBarColor,
  getRoDetailSlaBarPct,
  getRoDetailStepperStyle,
} from "../utils/inRepairRoDetailUtils";

const RoDetail = ({ onBack, isClosePending = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const roId = searchParams.get(RO_DETAIL_QUERY_ID) || "";
  const roNumberFromQuery = searchParams.get(RO_DETAIL_QUERY_RO) || "";
  const backLabel = getRoDetailBackLabel(location.pathname);
  const { repairOrderRagDetail: data, repairOrderRagDetailLoading: loading } =
    useSelector((state) => state.inventories);

  useEffect(() => {
    if (roId) {
      dispatch(getRepairOrderRAGDetail({ id: roId }));
    }
  }, [dispatch, roId]);

  const hasDetailData = Boolean(data);

  const header = data?.header;
  const phaseSla = data?.phase_sla;
  const repairDetails = data?.repair_details;
  const dates = data?.dates;
  const partPerformance = data?.part_performance;
  const commercial = data?.commercial;
  const suggestedAction = data?.suggested_action;
  const phaseHistory = data?.phase_history || [];
  const priorityDetail = data?.priority_detail;
  const workflowStepper = hasDetailData
    ? data?.workflow_stepper || []
    : RO_DETAIL_DEFAULT_WORKFLOW_STEPPER;
  const slaBorderColor = hasDetailData
    ? getRoDetailSlaBarColor(phaseSla?.rag_color)
    : RO_DETAIL_COLORS.suggestedActionBorder;
  const slaBarPct = hasDetailData ? getRoDetailSlaBarPct(phaseSla) : 0;
  const subtitleParts = hasDetailData
    ? getRoDetailSubtitleParts(data)
    : { description: "", partNumber: "", serialNumber: "" };
  const hasSubtitle =
    Boolean(subtitleParts.description) ||
    Boolean(subtitleParts.partNumber) ||
    Boolean(subtitleParts.serialNumber);
  const partRows = Array.isArray(data?.[RO_DETAIL_PART_NUMBERS_KEY])
    ? data[RO_DETAIL_PART_NUMBERS_KEY]
    : [];
  const emptyInfoMessage =
    !hasDetailData && !loading
      ? !roId
        ? RO_DETAIL_EMPTY_MESSAGES.noSelection
        : RO_DETAIL_EMPTY_MESSAGES.notFound
      : null;

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: RO_DETAIL_COLORS.pageBg,
        minHeight: "100%",
        height: "100%",
        overflow: "auto",
        pb: 2,
      }}
    >
      {loading && <LoadingOverlay />}

      <Box sx={{ px: RO_DETAIL_LAYOUT.pagePaddingX, pt: RO_DETAIL_LAYOUT.pagePaddingTop }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  minWidth: 0,
                }}
              >
                <Box
                  onClick={isClosePending ? undefined : onBack}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: RO_DETAIL_FONT.backLink,
                    color: RO_DETAIL_COLORS.link,
                    cursor: isClosePending ? "wait" : "pointer",
                    flexShrink: 0,
                    minHeight: 18,
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 14 }} />
                  Back to {backLabel}
                  {isClosePending ? (
                    <ButtonLoader color="var(--bondiblue-color)" size={14} />
                  ) : null}
                </Box>
                {emptyInfoMessage ? (
                  <Typography
                    sx={{
                      fontSize: RO_DETAIL_FONT.infoNote,
                      color: RO_DETAIL_COLORS.mutedText,
                      lineHeight: 1.4,
                    }}
                  >
                    {emptyInfoMessage}
                  </Typography>
                ) : null}
              </Box>
              <IconButton
                aria-label="Close"
                onClick={isClosePending ? undefined : onBack}
                disabled={isClosePending}
                size="small"
                sx={{
                  color: RO_DETAIL_COLORS.primaryText,
                  flexShrink: 0,
                  cursor: isClosePending ? "wait" : "pointer",
                }}
              >
                {isClosePending ? (
                  <ButtonLoader color="var(--bondiblue-color)" size={18} />
                ) : (
                  <CloseIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: "3px",
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: RO_DETAIL_FONT.pageTitle,
                  fontWeight: "var(--font-semibold)",
                  color: RO_DETAIL_COLORS.navy,
                }}
              >
                {data?.ro_number || roNumberFromQuery || "\u2014"}
              </Typography>
              {header?.rag_status && <InRepairStatusBadge label={header.rag_status} />}
              {header?.priority_tier && (
                <InRepairPriorityBadge tier={header.priority_tier} />
              )}
              {header?.header_badge && (
                <InRepairScGroupBadge value={header.header_badge} />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: RO_DETAIL_FONT.subtitleEmphasis,
                fontWeight: "var(--font-bold)",
                color: RO_DETAIL_COLORS.black,
                mt: "2px",
              }}
            >
              {hasSubtitle
                ? [
                    subtitleParts.description,
                    subtitleParts.partNumber &&
                      `P/N: ${subtitleParts.partNumber}`,
                    subtitleParts.serialNumber &&
                      `S/N: ${subtitleParts.serialNumber}`,
                  ]
                    .filter(Boolean)
                    .join(" \u00b7 ")
                : "\u2014"}
            </Typography>
          </Box>

          <Box sx={{ px: RO_DETAIL_LAYOUT.pagePaddingX, pt: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                borderRadius: RO_DETAIL_LAYOUT.cardRadius,
                overflow: "hidden",
                gap: 0,
              }}
            >
              {workflowStepper.map((step, index) => {
                const style = getRoDetailStepperStyle(step.status, step.phase);
                const isActive = step.status === "active";
                return (
                  <Box
                    key={`${step.phase}-${index}`}
                    sx={{
                      flex: 1,
                      minWidth: "80px",
                      padding: "8px 4px",
                      textAlign: "center",
                      background: style.bg,
                      color: style.color,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: RO_DETAIL_FONT.stepperShort,
                        opacity: isActive ? 1 : 0.65,
                        display: "block",
                        mb: "1px",
                        color: "inherit",
                      }}
                    >
                      {step.phase?.split(" ")[0]}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: RO_DETAIL_FONT.stepperPhase,
                        fontWeight: "var(--font-semibold)",
                        color: "inherit",
                      }}
                    >
                      {step.phase}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: `1fr ${RO_DETAIL_LAYOUT.sidebarWidth}`,
              },
              mt: RO_DETAIL_LAYOUT.stepperContentGap,
            }}
          >
            <Box
              sx={{
                px: RO_DETAIL_LAYOUT.pagePaddingX,
                py: 1.5,
                backgroundColor: RO_DETAIL_COLORS.pageBg,
                borderRight: { lg: `1px solid ${RO_DETAIL_COLORS.border}` },
              }}
            >
              <RoDetailCard
                borderLeftColor={slaBorderColor}
                sx={{ mb: RO_DETAIL_LAYOUT.sectionGap }}
              >
                <Typography
                  sx={{
                    fontSize: RO_DETAIL_FONT.slaTitle,
                    fontWeight: "var(--font-semibold)",
                    color: RO_DETAIL_COLORS.mutedText,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {phaseSla?.phase_title ||
                    phaseSla?.phase ||
                    (!hasDetailData ? "Phase SLA" : "")}
                  {phaseSla?.ro_status_subtitle
                    ? ` \u2014 ${phaseSla.ro_status_subtitle}`
                    : ""}
                </Typography>
                <Typography
                  sx={{
                    fontSize: RO_DETAIL_FONT.slaMeta,
                    color: RO_DETAIL_COLORS.secondaryText,
                    mb: "5px",
                    mt: "3px",
                  }}
                >
                  {`EDD: ${fmtRoDetailDate(phaseSla?.current_edd, "Not set")} \u00b7 Opened: ${fmtRoDetailDate(phaseSla?.open_date)}`}
                </Typography>
                <Box
                  sx={{
                    height: RO_DETAIL_LAYOUT.slaBarHeight,
                    background: RO_DETAIL_COLORS.statBoxBg,
                    borderRadius: RO_DETAIL_LAYOUT.cardRadius,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: RO_DETAIL_LAYOUT.cardRadius,
                      width: `${slaBarPct}%`,
                      background: slaBorderColor,
                    }}
                  />
                </Box>
              </RoDetailCard>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: RO_DETAIL_LAYOUT.gridGap,
                  mb: RO_DETAIL_LAYOUT.sectionGap,
                }}
              >
                <RoDetailCard>
                  <RoDetailSectionLabel>Repair details</RoDetailSectionLabel>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: RO_DETAIL_LAYOUT.fieldGap,
                      fontVariantLigatures: "none"
                    }}
                  >
                    <RoDetailField label="Vendor" value={repairDetails?.vendor} />
                    <RoDetailField label="RVE / Owner" value={repairDetails?.rve_owner} />
                    <RoDetailField label="Workscope" value={repairDetails?.ro_workscope} />
                    <RoDetailField label="Application" value={repairDetails?.application} />
                    <RoDetailField label="RO Status" value={repairDetails?.ro_status} />
                    <RoDetailField
                      label="Serial No."
                      value={repairDetails?.serial_number || data?.serial_number}
                    />
                  </Box>
                </RoDetailCard>
                <RoDetailCard>
                  <RoDetailSectionLabel>Dates & timing</RoDetailSectionLabel>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: RO_DETAIL_LAYOUT.fieldGap,
                    }}
                  >
                    <RoDetailField label="Open date" value={fmtRoDetailDate(dates?.open_date)} />
                    <RoDetailField
                      label="Current EDD"
                      value={fmtRoDetailDate(dates?.current_edd, "Not set")}
                    />
                    <RoDetailField label="Age (days)" value={fmtRoDetailDays(dates?.age_days)} />
                    <RoDetailField
                      label="Days to EDD"
                      value={fmtRoDetailDaysToEdd(dates?.days_to_edd)}
                    />
                    <RoDetailField label="Supply Chain Group" value={dates?.sc_group} />
                    <RoDetailField
                      label="Avg vendor TAT"
                      value={fmtRoDetailDays(dates?.avg_vendor_tat_days)}
                    />
                  </Box>
                </RoDetailCard>
              </Box>

              <RoDetailCard sx={{ mb: RO_DETAIL_LAYOUT.sectionGap }}>
                <RoDetailSectionLabel>
                  Part performance &mdash; historical averages (last 12 months)
                </RoDetailSectionLabel>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: RO_DETAIL_LAYOUT.fieldGap,
                  }}
                >
                  {[
                    {
                      value: fmtRoDetailTatDays(partPerformance?.avg_tat_days),
                      label: "Avg TAT for PN",
                    },
                    {
                      value: fmtRoDetailCurrency(partPerformance?.avg_ro_cost),
                      label: "Avg RO Cost",
                    },
                    {
                      value: fmtRoDetailCurrency(partPerformance?.min_ro_cost),
                      label: "Min RO Cost (12M)",
                    },
                    {
                      value: fmtRoDetailCurrency(partPerformance?.max_ro_cost),
                      label: "Max RO Cost (12M)",
                    },
                  ].map((stat) => (
                    <Box
                      key={stat.label}
                      sx={{
                        background: RO_DETAIL_COLORS.statBoxBg,
                        border: `1px solid ${RO_DETAIL_COLORS.borderLight}`,
                        borderRadius: RO_DETAIL_LAYOUT.statBoxRadius,
                        padding: RO_DETAIL_LAYOUT.statBoxPadding,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: RO_DETAIL_FONT.statValue,
                          fontWeight: "var(--font-semibold)",
                          color: RO_DETAIL_COLORS.navy,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: RO_DETAIL_FONT.statCaption,
                          color: RO_DETAIL_COLORS.mutedText,
                          mt: "1px",
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography
                  sx={{
                    fontSize: RO_DETAIL_FONT.partNumberNote,
                    color: RO_DETAIL_COLORS.mutedText,
                    mt: "6px",
                  }}
                >
                  Part number:{" "}
                  <Box component="span">{data?.part_number || "\u2014"}</Box>
                </Typography>
              </RoDetailCard>

              <RoDetailCard sx={{ mb: RO_DETAIL_LAYOUT.sectionGap }}>
                <RoDetailSectionLabel>Commercial & repair economics</RoDetailSectionLabel>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                    gap: RO_DETAIL_LAYOUT.fieldGap,
                  }}
                >
                  <RoDetailField
                    label="Workstream"
                    value={
                      commercial?.commercial_workstream ? (
                        <InRepairWorkstreamBadge
                          label={commercial.commercial_workstream}
                        />
                      ) : undefined
                    }
                  />
                  <RoDetailField label="SO Customer" value={commercial?.so_customer} />
                  <RoDetailField label="SO Category" value={commercial?.so_category} />
                  <RoDetailField
                    label="Fair Market Value"
                    value={fmtRoDetailCurrency(commercial?.fmv)}
                  />
                  <RoDetailField
                    label="RO cost % FMV"
                    value={fmtRoDetailPercent(commercial?.ro_cost_pct_fmv)}
                  />
                  <RoDetailField
                    label="Economic flag"
                    value={commercial?.repair_economic_flag}
                    valueSx={{
                      color: getRoDetailEconFlagColor(commercial?.repair_economic_flag),
                    }}
                  />
                </Box>
              </RoDetailCard>

              <RoDetailCard borderLeftColor={RO_DETAIL_COLORS.suggestedActionBorder}>
                <RoDetailSectionLabel /* extra="(label content to be confirmed by client)" */>
                  Suggested action
                </RoDetailSectionLabel>
                <Typography
                  sx={{
                    fontSize: RO_DETAIL_FONT.suggestedAction,
                    fontWeight: 400,
                    color: RO_DETAIL_COLORS.primaryText,
                  }}
                >
                  {suggestedAction?.action || "\u2014"}
                </Typography>
                {suggestedAction?.reason ? (
                  <Typography
                    sx={{
                      fontSize: RO_DETAIL_FONT.suggestedReason,
                      color: RO_DETAIL_COLORS.secondaryText,
                      mt: "4px",
                    }}
                  >
                    Reason: {suggestedAction.reason}
                  </Typography>
                ) : null}
              </RoDetailCard>

              <RoDetailPartsTable
                rows={partRows}
                sx={{ mt: RO_DETAIL_LAYOUT.sectionGap }}
              />
            </Box>

            <Box
              sx={{
                px: 1.5,
                py: 1.5,
                backgroundColor: RO_DETAIL_COLORS.sidebarBg,
              }}
            >
              <RoDetailCard sx={{ mb: RO_DETAIL_LAYOUT.sectionGap }}>
                <RoDetailSectionLabel>Phase history</RoDetailSectionLabel>
                <Box>
                  {phaseHistory.map((phase, index) => {
                    const isLast = index === phaseHistory.length - 1;
                    const dim = phase.status === "pending";
                    const { datesLine, daysLine, isDaysOverSla } =
                      getRoDetailPhaseHistoryLines(phase);

                    return (
                      <Box
                        key={`${phase.phase}-${index}`}
                        sx={{
                          display: "flex",
                          gap: "7px",
                          alignItems: "center",
                          padding: "5px 0",
                          borderBottom: isLast
                            ? "none"
                            : `1px solid ${RO_DETAIL_COLORS.borderLight}`,
                          opacity: dim ? 0.4 : 1,
                        }}
                      >
                        <RoDetailPhaseHistoryIcon status={phase.status} />
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: RO_DETAIL_FONT.phaseHistoryTitle,
                              color: RO_DETAIL_COLORS.primaryText,
                            }}
                          >
                            {phase.phase}
                          </Typography>
                          {datesLine ? (
                            <Typography
                              sx={{
                                color: RO_DETAIL_COLORS.secondaryText,
                                fontSize: RO_DETAIL_FONT.phaseHistoryNote,
                              }}
                            >
                              {datesLine}
                            </Typography>
                          ) : null}
                          {daysLine ? (
                            <Typography
                              sx={{
                                color: isDaysOverSla
                                  ? IN_REPAIR_RAG_COLORS.red.text
                                  : RO_DETAIL_COLORS.secondaryText,
                                fontSize: RO_DETAIL_FONT.phaseHistoryNote,
                              }}
                            >
                              {daysLine}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </RoDetailCard>

              <RoDetailCard>
                <RoDetailSectionLabel>Priority detail</RoDetailSectionLabel>
                <Box sx={{ fontSize: RO_DETAIL_FONT.priorityRow }}>
                  <Box
                    sx={{
                      padding: "4px 0",
                      borderBottom: `1px solid ${RO_DETAIL_COLORS.borderLight}`,
                      color: RO_DETAIL_COLORS.primaryText,
                    }}
                  >
                    Tier: {priorityDetail?.priority_tier || "\u2014"}
                  </Box>
                  <Box
                    sx={{
                      padding: "4px 0",
                      borderBottom: `1px solid ${RO_DETAIL_COLORS.borderLight}`,
                      color: RO_DETAIL_COLORS.primaryText,
                    }}
                  >
                    Score: {fmtRoDetailNumber(priorityDetail?.priority_score)}
                  </Box>
                  <Box sx={{ padding: "4px 0", color: RO_DETAIL_COLORS.secondaryText }}>
                    {priorityDetail?.reason_for_priority}
                  </Box>
                </Box>
              </RoDetailCard>
            </Box>
          </Box>
    </Box>
  );
};

export default RoDetail;
